import React from "react";
import { AlertTriangle, Scan, ZoomIn, ZoomOut } from "lucide-react";
import { t } from "@client/lib/dictionary";
import { cn } from "@client/lib/cn";
import { useFoscamStore } from "@client/store/foscam";
import { useUserStore } from "@client/store/user";
import type { PtzHoldApi } from "@client/hooks/usePtzHold";
import type { UseDigitalZoomApi } from "@client/hooks/useDigitalZoom";
import { transportLabel, type Transport } from "@client/lib/foscam/streamStats";
import PresetBar from "./PresetBar";
import PtzPad from "./PtzPad";

/**
 * Ovládací plocha kamery — PTZ kříž, zoom, uložené pozice a stavová zpětná vazba.
 *
 * **Jeden layout pro všechny fullscreen režimy.** Dřív existovaly varianty `bar`
 * (lišta pod obrazem) a `overlay` (přes obraz) a rozhodoval mezi nimi výpočet
 * letterboxu — chování se tak měnilo podle poměru stran kamery a orientace telefonu.
 * Teď je to vždycky overlay ukotvený na spodní hranu DLAŽDICE (ne obrazu), takže na
 * vysoké obrazovce sedí v černém pruhu pod videem a na široké přes jeho spodek.
 * Jedno chování ve spotlightu i ve fullscreenu wrapperu.
 *
 * **Nikdy nezmizí.** Po nečinnosti se jen ztlumí na `IDLE_OPACITY` (viz `useAutoHide`)
 * a zůstává klikatelné — dřív se schovávalo na `opacity: 0` s vypnutými pointer-events,
 * takže uživatel nevěděl, že tam něco je.
 *
 * Komponenta se renderuje jen tam, kde se skutečně ovládá (spotlight, fullscreen
 * wrapperu) — v mřížce čtyř kamer má dlaždice pouze tlačítko fullscreenu.
 */

export interface CameraControlsProps {
  slug: string;
  /**
   * PTZ držení vlastní `CameraTile`, ne tahle komponenta.
   *
   * Kameru ovládá i edge-push digitálního zoomu a `ptzStopRun` je na kameře globální —
   * musí proto existovat jediná instance, která hlídá „jedna osa v jednu chvíli".
   */
  hold: PtzHoldApi;
  /** Digitální zoom — tlačítka jsou zároveň jediná klávesnicí ovladatelná cesta k němu. */
  zoom: UseDigitalZoomApi;
  /** Přenos + latence do chipu. Bez měření se ukáže jen název přenosu. */
  transport: Transport;
  measuredMs: number | null;
  /** 1 při interakci, `IDLE_OPACITY` v klidu. */
  opacity: number;
  /** Dlaždice je ve svislém snap-scrolleru → tlačítka musí pustit scroll. */
  scrollAxis: "stack" | "none";
  /** Interakce → reset odpočtu ztlumení. */
  onPoke?: () => void;
  /** Hlásí nahoru, že se něco děje (držení / dialog) → nesmí se ztlumit. */
  onBusyChange?: (busy: boolean) => void;
  className?: string;
}

export const CameraControls: React.FC<CameraControlsProps> = ({
  slug,
  hold,
  zoom,
  transport,
  measuredMs,
  opacity,
  scrollAxis,
  onPoke,
  onBusyChange,
  className,
}) => {
  const user = useUserStore((s) => s.user);
  const isAdmin = typeof user === "object" && user !== null && (user as { role: string }).role === "ADMIN";
  const caps = useFoscamStore((s) => s.caps[slug]);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  // Držení ani otevřený dialog se nesmí ztlumit pod rukou.
  React.useEffect(() => {
    onBusyChange?.(hold.busy || dialogOpen);
  }, [hold.busy, dialogOpen, onBusyChange]);

  // Bez informací o schopnostech (mimo RPi / neautorizováno) se ovládání neukazuje.
  if (!caps) return null;

  const hasControls = caps.online && (caps.hasPtz || caps.hasZoom || caps.hasPresets);

  // Shell je průchozí pro pointer — klikatelné jsou jen samotné ovládací bloky,
  // aby tap do prázdna nad obrazem propadl na video a jen probudil chrome.
  const shell = cn("pointer-events-none absolute inset-x-0 bottom-0 z-20 flex flex-col gap-2 p-2", className);
  // Menší než PTZ tlačítka (36 vs 44 px): digitální zoom není fyzická akce, nedrží se
  // a omyl u něj nic nestojí — hlavní plochu si zaslouží kříž.
  const zoomBtn = cn(
    "inline-flex h-9 w-9 items-center justify-center rounded-md text-white",
    "bg-black/55 hover:bg-black/75 active:bg-black/85 backdrop-blur-sm",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring select-none touch-manipulation",
    "disabled:cursor-default disabled:hover:bg-black/55"
  );
  const style = {
    opacity,
    transition: "opacity 200ms ease-out",
    // Home indicator na iPhonu. Inline, ne Tailwind arbitrary value: `max()` s `env()`
    // uvnitř třídy neprojde CSS minifikátorem (lightningcss) při produkčním buildu.
    paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))",
  } as const;

  if (!caps.online) {
    return (
      <div className={shell} style={style}>
        <div className="pointer-events-auto flex items-center gap-2 self-start rounded-md bg-black/70 px-2 py-1 text-xs text-white">
          <AlertTriangle className="h-3.5 w-3.5 text-status-warning" />
          {t.foscam.controlUnavailable}
        </div>
      </div>
    );
  }

  if (!hasControls) return null;

  return (
    <div className={shell} style={style} onPointerMove={onPoke}>
      {/* Horní řádek: uložené pozice + stav držení a přenos. */}
      <div className="pointer-events-auto flex flex-wrap items-center justify-between gap-2">
        {caps.hasPresets ? (
          <PresetBar slug={slug} isAdmin={isAdmin} onPoke={onPoke} onDialogChange={setDialogOpen} />
        ) : (
          <span />
        )}

        <div className="flex items-center gap-2">
          {hold.label && (
            <span
              className={cn(
                "rounded-md px-2 py-0.5 text-xs font-semibold tabular-nums text-white backdrop-blur-sm",
                hold.tone === "danger" ? "bg-destructive/90" : "bg-black/70"
              )}
              role="status"
              aria-live="polite"
            >
              {hold.label}
            </span>
          )}
          {zoom.isZoomed && (
            <span
              className="rounded-md bg-black/60 px-2 py-0.5 text-[10px] text-white/80 tabular-nums backdrop-blur-sm"
              title={t.foscam.zoomHint}
            >
              {t.foscam.zoomLevel(zoom.scale)}
            </span>
          )}
          <span
            className="rounded-md bg-black/60 px-2 py-0.5 text-[10px] text-white/80 tabular-nums backdrop-blur-sm"
            title={t.foscam.transportHint}
          >
            {transportLabel(transport, measuredMs)}
          </span>
        </div>
      </div>

      {/* Dolní řádek: směrový kříž + optický zoom vlevo, digitální zoom vpravo. */}
      <div className="pointer-events-auto flex items-end justify-between gap-2">
        <PtzPad caps={caps} hold={hold} scrollAxis={scrollAxis} onPoke={onPoke} />

        {/* Digitální zoom má tlačítka i tam, kde jde ovládat gesty — jsou to jediné
            prvky, přes které se k němu dostane klávesnice a čtečka obrazovky. */}
        <div className="flex flex-col gap-1">
          <button
            type="button"
            className={zoomBtn}
            aria-label={t.foscam.digitalZoomIn}
            title={t.foscam.digitalZoomIn}
            onPointerEnter={onPoke}
            onClick={() => {
              onPoke?.();
              zoom.zoomBy(1.4);
            }}
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={zoomBtn}
            aria-label={t.foscam.digitalZoomOut}
            title={t.foscam.digitalZoomOut}
            onPointerEnter={onPoke}
            onClick={() => {
              onPoke?.();
              zoom.zoomBy(1 / 1.4);
            }}
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={cn(zoomBtn, !zoom.isZoomed && "opacity-40")}
            disabled={!zoom.isZoomed}
            aria-label={t.foscam.zoomReset}
            title={t.foscam.zoomReset}
            onPointerEnter={onPoke}
            onClick={() => {
              onPoke?.();
              zoom.resetZoom();
            }}
          >
            <Scan className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Periferní indikace držení u hrany — vidíš ji i když je prstenec pod prstem. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 h-0.5 bg-primary transition-[width] duration-100"
        style={{ width: `${Math.round(hold.progress * 100)}%`, opacity: hold.busy ? 1 : 0 }}
      />
    </div>
  );
};

export default CameraControls;
